import {
  Avatar,
  Box,
  Button,
  Card,
  Container,
  Divider,
  Grid,
  Group,
  LoadingOverlay,
  Text,
  Title
} from '@mantine/core'
import Link from 'next/link'
import { useRouter } from 'next/router'

import { Redirect } from '@/components/commons'
import { SocialIcons } from '@/components/widgets'
import { useFetch } from '@/lib/hooks'
import { getUrlString } from '@/lib/utils'
import { Partner as PartnerType, Site as SiteType } from '@/types'

import { ContactCard } from './components'

interface Props {
  initialData?: {
    site: SiteType
  }
}

export default function ContactsView({ initialData }: Props) {
  // Hooks
  const router = useRouter()

  // Constants
  const { site } = router.query || {}
  const siteSlug = getUrlString(site)

  // Fetch
  const { data: siteData, error } = useFetch([siteSlug ? `/${siteSlug}/` : null], {
    fallbackData: initialData?.site
  })

  const isLoading = !siteData && !error

  if (isLoading) {
    return <LoadingOverlay visible />
  }

  const partners = siteData?.supportConfiguration?.partners?.filter(
    (partner: PartnerType) =>
      partner.supportConfiguration.contacts.length > 0 ||
      (partner.supportConfiguration.contacts.length === 0 && partner.supportConfiguration.isActive)
  )

  const displayContacts =
    Array.isArray(siteData?.supportConfiguration?.contacts) &&
    !!siteData.supportConfiguration.contacts.length
  const displayPartnersContacts =
    siteData?.supportConfiguration?.displayPartnersContacts && partners.length > 0
  const displaySocialLinks = Array.isArray(siteData?.links) && !!siteData.links.length

  const renderPartnersContacts = () => {
    return (
      <Box my="xl">
        <Title order={2}>Nossos Parceiros</Title>
        <Text color="dimmed" mb="md">
          Consulte os canais de atendimento dos nossos parceiros
        </Text>
        <Grid>
          {partners.map((partner: PartnerType) => (
            <Grid.Col sm={6} md={4} key={partner.uid}>
              <Card mb="sm" withBorder>
                <Group spacing={4} mb="md">
                  <Avatar src={partner.seller?.logo} alt={partner.seller?.displayName}></Avatar>
                  <Title order={3}>{partner.seller?.displayName}</Title>
                </Group>
                {partner.supportConfiguration?.contacts.length > 0 ? (
                  <ContactCard contacts={partner.supportConfiguration.contacts} />
                ) : (
                  <Text mb={27}>
                    Este vendedor não disponibiliza contato por e-mail ou telefone.
                    {partner.supportConfiguration?.isActive
                      ? ' Abra um atendimento pelo botão abaixo:'
                      : ''}
                  </Text>
                )}
                {partner.supportConfiguration?.isActive && (
                  <Button component={Link} href={`/help/tickets?seller=${partner.seller.slug}`}>
                    Abrir atendimento
                  </Button>
                )}
              </Card>
            </Grid.Col>
          ))}
        </Grid>
      </Box>
    )
  }

  if (!displayContacts && !displayPartnersContacts) return <Redirect url="/help/tickets" replace />

  return (
    <Container px="xs" py="lg" size="xl">
      <Title mb="lg">Fale Conosco</Title>
      <Box mb="xl">
        <ContactCard contacts={siteData?.supportConfiguration.contacts} />
        {siteData?.supportConfiguration.isActive && (
          <Button component={Link} href={`/help/tickets?seller=${siteData?.seller?.slug}`}>
            Abrir atendimento
          </Button>
        )}
      </Box>
      <Divider />
      {displayPartnersContacts && renderPartnersContacts()}
      {displaySocialLinks && (
        <>
          <Title order={2} mb="md">
            Siga-nos
          </Title>
          <SocialIcons links={siteData?.links} size="xl" />
        </>
      )}
    </Container>
  )
}
